import React, { useState, useEffect } from 'react';
import ApiClient from "../utils/ApiClient";

export default function XeroSettings() {
    const [accounts, setAccounts] = useState([]);
    const [contactMappings, setContactMappings] = useState([]);
    const [settings, setSettings] = useState([]);
    const [accountCodes, setAccountCodes] = useState({ dorado: [], rhino: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Supplier mappings state
    const [suppliers, setSuppliers] = useState([]);
    const [supplierMappings, setSupplierMappings] = useState([]);
    const [configuringSupplier, setConfiguringSupplier] = useState(null); // { supplierId: 63, supplierName: 'Schluter' }
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [supplierSearchResults, setSupplierSearchResults] = useState([]);
    const [searchingSupplierContact, setSearchingSupplierContact] = useState(false);

    // Contact search state
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchingFor, setSearchingFor] = useState(null); // { account: 'dorado', role: 'schluter' }
    const [searching, setSearching] = useState(false);

    // Account code search state
    const [accountCodeSearchTerm, setAccountCodeSearchTerm] = useState('');
    const [accountCodeResults, setAccountCodeResults] = useState([]);
    const [selectingAccountCode, setSelectingAccountCode] = useState(null); // { account: 'dorado', settingKey: 'bill_account_code' }
    const [loadingAccountCodes, setLoadingAccountCodes] = useState(false);

    // Tax type selection state
    const [selectingTaxType, setSelectingTaxType] = useState(null); // { account: 'dorado', settingKey: 'bill_tax_type' }
    const [taxTypeOptions, setTaxTypeOptions] = useState([]);
    const [loadingTaxTypes, setLoadingTaxTypes] = useState(false);

    // URL params for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const connectedAccount = urlParams.get('connected');
    const oauthError = urlParams.get('error');

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (connectedAccount) {
            // Clear URL params and show success message
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [connectedAccount]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [accountsRes, mappingsRes, settingsRes, suppliersRes, supplierMappingsRes] = await Promise.all([
                ApiClient.get('/api/xero/accounts'),
                ApiClient.get('/api/xero/contacts/mappings'),
                ApiClient.get('/api/xero/settings'),
                ApiClient.get('/api/xero/suppliers'),
                ApiClient.get('/api/xero/contacts/supplier-mappings')
            ]);
            setAccounts(accountsRes.data);
            setContactMappings(mappingsRes.data);
            setSettings(settingsRes.data);
            setSuppliers(suppliersRes.data);
            setSupplierMappings(supplierMappingsRes.data);

            // Fetch account codes for connected accounts
            for (const account of accountsRes.data) {
                try {
                    const codesRes = await ApiClient.get(`/api/xero/account-codes/${account.account_name}`);
                    setAccountCodes(prev => ({ ...prev, [account.account_name]: codesRes.data }));
                } catch (e) {
                    console.log(`Could not fetch account codes for ${account.account_name}`);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = async (accountName) => {
        // Fetch the Xero authorize URL through ApiClient (carries the JWT) and then
        // redirect - a bare navigation to /api/xero/connect has no auth header and gets 403
        try {
            const res = await ApiClient.get('/api/xero/connect-url', { params: { accountName } });
            window.location.href = res.data.url;
        } catch (err) {
            alert('Error starting Xero connection: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleDisconnect = async (accountName) => {
        if (!window.confirm(`Are you sure you want to disconnect ${accountName}?`)) return;

        try {
            await ApiClient.delete(`/api/xero/accounts/${accountName}`);
            await fetchData();
        } catch (err) {
            alert('Error disconnecting: ' + err.message);
        }
    };

    const handleSearchContacts = async () => {
        if (!searchingFor || !searchTerm.trim()) return;

        setSearching(true);
        try {
            const res = await ApiClient.get(`/api/xero/contacts/search?accountName=${searchingFor.account}&searchTerm=${encodeURIComponent(searchTerm)}`);
            setSearchResults(res.data);
        } catch (err) {
            alert('Error searching contacts: ' + err.message);
        } finally {
            setSearching(false);
        }
    };

    const handleSelectContact = async (contact) => {
        try {
            await ApiClient.post('/api/xero/contacts/mapping', {
                xeroAccount: searchingFor.account,
                contactRole: searchingFor.role,
                xeroContactId: contact.contactId,
                xeroContactName: contact.name
            });
            setSearchingFor(null);
            setSearchTerm('');
            setSearchResults([]);
            await fetchData();
        } catch (err) {
            alert('Error saving contact: ' + err.message);
        }
    };

    const handleUpdateSetting = async (xeroAccount, settingKey, settingValue) => {
        try {
            await ApiClient.put('/api/xero/settings', {
                xeroAccount,
                settingKey,
                settingValue
            });
            await fetchData();
        } catch (err) {
            alert('Error saving setting: ' + err.message);
        }
    };

    const handleOpenAccountCodeSearch = async (account, settingKey) => {
        setSelectingAccountCode({ account, settingKey });
        setAccountCodeSearchTerm('');
        setAccountCodeResults([]);
        setLoadingAccountCodes(true);
        try {
            const res = await ApiClient.get(`/api/xero/account-codes/${account}`);
            setAccountCodeResults(res.data);
        } catch (err) {
            console.log('Error fetching account codes:', err.message);
            setAccountCodeResults([]);
        } finally {
            setLoadingAccountCodes(false);
        }
    };

    const handleSelectAccountCode = async (code) => {
        try {
            await ApiClient.put('/api/xero/settings', {
                xeroAccount: selectingAccountCode.account,
                settingKey: selectingAccountCode.settingKey,
                settingValue: code.code
            });
            setSelectingAccountCode(null);
            setAccountCodeSearchTerm('');
            setAccountCodeResults([]);
            await fetchData();
        } catch (err) {
            alert('Error saving account code: ' + err.message);
        }
    };

    const handleOpenTaxTypeSearch = async (account, settingKey) => {
        setSelectingTaxType({ account, settingKey });
        setTaxTypeOptions([]);
        setLoadingTaxTypes(true);
        try {
            const res = await ApiClient.get(`/api/xero/tax-rates/${account}`);
            setTaxTypeOptions(res.data);
        } catch (err) {
            console.log('Error fetching tax rates:', err.message);
            setTaxTypeOptions([]);
        } finally {
            setLoadingTaxTypes(false);
        }
    };

    const handleSelectTaxType = async (taxType) => {
        try {
            // Save both the code and the display name
            await ApiClient.put('/api/xero/settings', {
                xeroAccount: selectingTaxType.account,
                settingKey: selectingTaxType.settingKey,
                settingValue: taxType.code
            });
            // Save the name separately for display
            await ApiClient.put('/api/xero/settings', {
                xeroAccount: selectingTaxType.account,
                settingKey: selectingTaxType.settingKey + '_name',
                settingValue: taxType.name
            });
            setSelectingTaxType(null);
            setTaxTypeOptions([]);
            await fetchData();
        } catch (err) {
            alert('Error saving tax type: ' + err.message);
        }
    };

    const filteredAccountCodes = accountCodeResults.filter(code => {
        if (!accountCodeSearchTerm) return true;
        const search = accountCodeSearchTerm.toLowerCase();
        return code.code.toLowerCase().includes(search) || code.name.toLowerCase().includes(search);
    });

    // Supplier contact mapping handlers
    const handleSearchSupplierContacts = async () => {
        if (!configuringSupplier || !supplierSearchTerm.trim()) return;

        setSearchingSupplierContact(true);
        try {
            const res = await ApiClient.get(`/api/xero/contacts/search?accountName=dorado&searchTerm=${encodeURIComponent(supplierSearchTerm)}`);
            setSupplierSearchResults(res.data);
        } catch (err) {
            alert('Error searching contacts: ' + err.message);
        } finally {
            setSearchingSupplierContact(false);
        }
    };

    const handleSelectSupplierContact = async (contact) => {
        try {
            await ApiClient.post('/api/xero/contacts/supplier-mapping', {
                xeroAccount: 'dorado',
                supplierId: configuringSupplier.supplierId,
                xeroContactId: contact.contactId,
                xeroContactName: contact.name
            });
            setConfiguringSupplier(null);
            setSupplierSearchTerm('');
            setSupplierSearchResults([]);
            await fetchData();
        } catch (err) {
            alert('Error saving supplier contact mapping: ' + err.message);
        }
    };

    const handleDeleteSupplierMapping = async (supplierId) => {
        if (!window.confirm('Are you sure you want to remove this supplier mapping?')) return;

        try {
            await ApiClient.delete(`/api/xero/contacts/supplier-mapping?xeroAccount=dorado&supplierId=${supplierId}`);
            await fetchData();
        } catch (err) {
            alert('Error deleting mapping: ' + err.message);
        }
    };

    const getSupplierMapping = (supplierId) => {
        return supplierMappings.find(m => m.supplier_id === supplierId && m.xero_account === 'dorado');
    };

    const getContactMapping = (account, role) => {
        return contactMappings.find(m => m.xero_account === account && m.contact_role === role);
    };

    const getSetting = (account, key) => {
        const setting = settings.find(s => s.xero_account === account && s.setting_key === key);
        return setting?.setting_value || '';
    };

    const isAccountConnected = (accountName) => {
        return accounts.some(a => a.account_name === accountName);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-gray-600">Loading...</div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto py-8">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-2xl font-semibold text-gray-900">Xero Settings</h1>
                <p className="mt-2 text-sm text-gray-700">
                    Configure Xero connections and contact mappings
                </p>
            </div>

            {/* Success/Error Messages */}
            {connectedAccount && (
                <div className="mb-6 rounded-md bg-green-50 p-4">
                    <div className="flex">
                        <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">
                                Successfully connected {connectedAccount}!
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {oauthError && (
                <div className="mb-6 rounded-md bg-red-50 p-4">
                    <div className="flex">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-red-800">
                                Error connecting to Xero: {oauthError}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Connected Accounts Section */}
            <div className="bg-white shadow rounded-lg mb-8">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Connected Accounts</h3>
                </div>
                <div className="px-4 py-5 sm:p-6">
                    <div className="space-y-4">
                        {/* Dorado Distribution */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                                <div className={`w-3 h-3 rounded-full mr-3 ${isAccountConnected('dorado') ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <div>
                                    <p className="font-medium text-gray-900">Dorado Distribution</p>
                                    <p className="text-sm text-gray-500">
                                        {isAccountConnected('dorado') ? 'Connected' : 'Not connected'}
                                    </p>
                                </div>
                            </div>
                            {isAccountConnected('dorado') ? (
                                <div className="flex gap-2">
                                    {/* Re-runs OAuth and overwrites the stored tokens in place - use when Xero
                                        refuses access (403) even though the account still shows Connected */}
                                    <button
                                        onClick={() => handleConnect('dorado')}
                                        className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
                                    >
                                        Reconnect
                                    </button>
                                    <button
                                        onClick={() => handleDisconnect('dorado')}
                                        className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                                    >
                                        Disconnect
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleConnect('dorado')}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                    Connect
                                </button>
                            )}
                        </div>

                        {/* Retail Rhino */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                                <div className={`w-3 h-3 rounded-full mr-3 ${isAccountConnected('rhino') ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <div>
                                    <p className="font-medium text-gray-900">Retail Rhino</p>
                                    <p className="text-sm text-gray-500">
                                        {isAccountConnected('rhino') ? 'Connected' : 'Not connected'}
                                    </p>
                                </div>
                            </div>
                            {isAccountConnected('rhino') ? (
                                <div className="flex gap-2">
                                    {/* Re-runs OAuth and overwrites the stored tokens in place - use when Xero
                                        refuses access (403) even though the account still shows Connected */}
                                    <button
                                        onClick={() => handleConnect('rhino')}
                                        className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200"
                                    >
                                        Reconnect
                                    </button>
                                    <button
                                        onClick={() => handleDisconnect('rhino')}
                                        className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                                    >
                                        Disconnect
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => handleConnect('rhino')}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                    Connect
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Mappings Section */}
            <div className="bg-white shadow rounded-lg mb-8">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Contact Mappings</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Select which Xero contacts to use for each role
                    </p>
                </div>
                <div className="px-4 py-5 sm:p-6">
                    {/* Dorado Contacts */}
                    {isAccountConnected('dorado') && (
                        <div className="mb-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Dorado Distribution</h4>
                            <div className="space-y-3">
                                <ContactMappingRow
                                    label="Schluter (Supplier)"
                                    mapping={getContactMapping('dorado', 'schluter')}
                                    onConfigure={() => setSearchingFor({ account: 'dorado', role: 'schluter' })}
                                />
                                <ContactMappingRow
                                    label="Retail Rhino (Customer)"
                                    mapping={getContactMapping('dorado', 'rhino')}
                                    onConfigure={() => setSearchingFor({ account: 'dorado', role: 'rhino' })}
                                />
                            </div>
                        </div>
                    )}

                    {/* Rhino Contacts */}
                    {isAccountConnected('rhino') && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Retail Rhino</h4>
                            <div className="space-y-3">
                                <ContactMappingRow
                                    label="Dorado Distribution (Supplier)"
                                    mapping={getContactMapping('rhino', 'dorado')}
                                    onConfigure={() => setSearchingFor({ account: 'rhino', role: 'dorado' })}
                                />
                            </div>
                        </div>
                    )}

                    {!isAccountConnected('dorado') && !isAccountConnected('rhino') && (
                        <p className="text-sm text-gray-500">Connect a Xero account to configure contact mappings.</p>
                    )}
                </div>
            </div>

            {/* Supplier Contact Mappings Section */}
            {isAccountConnected('dorado') && (
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Supplier Contact Mappings</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            Map suppliers to their Xero contacts in Dorado Distribution for multi-supplier invoice syncing
                        </p>
                    </div>
                    <div className="px-4 py-5 sm:p-6">
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {suppliers.map(supplier => {
                                const mapping = getSupplierMapping(supplier.id);
                                return (
                                    <div key={supplier.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-700">{supplier.name}</p>
                                            {mapping ? (
                                                <p className="text-sm text-green-600">{mapping.xero_contact_name}</p>
                                            ) : (
                                                <p className="text-sm text-gray-400">Not configured</p>
                                            )}
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => setConfiguringSupplier({ supplierId: supplier.id, supplierName: supplier.name })}
                                                className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded hover:bg-indigo-200"
                                            >
                                                {mapping ? 'Change' : 'Configure'}
                                            </button>
                                            {mapping && (
                                                <button
                                                    onClick={() => handleDeleteSupplierMapping(supplier.id)}
                                                    className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {suppliers.length === 0 && (
                                <p className="text-sm text-gray-500 text-center py-4">No suppliers found in the database.</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Account Codes Section */}
            <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Account Codes</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        Configure which account codes to use for bills and invoices
                    </p>
                </div>
                <div className="px-4 py-5 sm:p-6">
                    {/* Dorado Account Codes */}
                    {isAccountConnected('dorado') && (
                        <div className="mb-6">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Dorado Distribution</h4>
                            <div className="space-y-3">
                                <AccountCodeRow
                                    label="Bill Account Code"
                                    currentCode={getSetting('dorado', 'bill_account_code')}
                                    onConfigure={() => handleOpenAccountCodeSearch('dorado', 'bill_account_code')}
                                />
                                <TaxTypeRow
                                    label="Bill Tax Type"
                                    currentCode={getSetting('dorado', 'bill_tax_type')}
                                    currentName={getSetting('dorado', 'bill_tax_type_name')}
                                    onConfigure={() => handleOpenTaxTypeSearch('dorado', 'bill_tax_type')}
                                />
                                <AccountCodeRow
                                    label="Invoice Account Code"
                                    currentCode={getSetting('dorado', 'invoice_account_code')}
                                    onConfigure={() => handleOpenAccountCodeSearch('dorado', 'invoice_account_code')}
                                />
                                <TaxTypeRow
                                    label="Invoice Tax Type"
                                    currentCode={getSetting('dorado', 'invoice_tax_type')}
                                    currentName={getSetting('dorado', 'invoice_tax_type_name')}
                                    onConfigure={() => handleOpenTaxTypeSearch('dorado', 'invoice_tax_type')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Rhino Account Codes */}
                    {isAccountConnected('rhino') && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Retail Rhino</h4>
                            <div className="space-y-3">
                                <AccountCodeRow
                                    label="Bill Account Code"
                                    currentCode={getSetting('rhino', 'bill_account_code')}
                                    onConfigure={() => handleOpenAccountCodeSearch('rhino', 'bill_account_code')}
                                />
                            </div>
                        </div>
                    )}

                    {!isAccountConnected('dorado') && !isAccountConnected('rhino') && (
                        <p className="text-sm text-gray-500">Connect a Xero account to configure account codes.</p>
                    )}
                </div>
            </div>

            {/* Contact Search Modal */}
            {searchingFor && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setSearchingFor(null)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Select Contact for {searchingFor.role.charAt(0).toUpperCase() + searchingFor.role.slice(1)}
                            </h3>

                            <div className="flex space-x-2 mb-4">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearchContacts()}
                                    placeholder="Search contacts..."
                                    className="flex-1 rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                                    autoFocus
                                />
                                <button
                                    onClick={handleSearchContacts}
                                    disabled={searching}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {searching ? 'Searching...' : 'Search'}
                                </button>
                            </div>

                            <div className="max-h-64 overflow-y-auto">
                                {searchResults.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">
                                        {searchTerm ? 'No contacts found' : 'Enter a search term to find contacts'}
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-gray-200">
                                        {searchResults.map(contact => (
                                            <li
                                                key={contact.contactId}
                                                onClick={() => handleSelectContact(contact)}
                                                className="py-3 px-2 hover:bg-gray-50 cursor-pointer rounded"
                                            >
                                                <p className="font-medium text-gray-900">{contact.name}</p>
                                                <p className="text-xs text-gray-400 font-mono">{contact.contactId}</p>
                                                {contact.emailAddress && (
                                                    <p className="text-sm text-gray-500">{contact.emailAddress}</p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => {
                                        setSearchingFor(null);
                                        setSearchTerm('');
                                        setSearchResults([]);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Account Code Search Modal */}
            {selectingAccountCode && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setSelectingAccountCode(null)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Select Account Code
                            </h3>

                            <div className="mb-4">
                                <input
                                    type="text"
                                    value={accountCodeSearchTerm}
                                    onChange={(e) => setAccountCodeSearchTerm(e.target.value)}
                                    placeholder="Filter account codes..."
                                    className="w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                                    autoFocus
                                />
                            </div>

                            <div className="max-h-64 overflow-y-auto">
                                {loadingAccountCodes ? (
                                    <p className="text-sm text-gray-500 text-center py-4">Loading account codes...</p>
                                ) : filteredAccountCodes.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">
                                        {accountCodeResults.length === 0 ? 'No account codes available. Please reconnect to Xero.' : 'No matching account codes'}
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-gray-200">
                                        {filteredAccountCodes.map(code => (
                                            <li
                                                key={code.code}
                                                onClick={() => handleSelectAccountCode(code)}
                                                className="py-3 px-2 hover:bg-gray-50 cursor-pointer rounded"
                                            >
                                                <p className="font-medium text-gray-900">{code.code}</p>
                                                <p className="text-sm text-gray-500">{code.name}</p>
                                                <p className="text-xs text-gray-400">{code.type}</p>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => {
                                        setSelectingAccountCode(null);
                                        setAccountCodeSearchTerm('');
                                        setAccountCodeResults([]);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Supplier Contact Search Modal */}
            {configuringSupplier && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setConfiguringSupplier(null)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Select Xero Contact for {configuringSupplier.supplierName}
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Search for the corresponding contact in Dorado Distribution's Xero account
                            </p>

                            <div className="flex space-x-2 mb-4">
                                <input
                                    type="text"
                                    value={supplierSearchTerm}
                                    onChange={(e) => setSupplierSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearchSupplierContacts()}
                                    placeholder="Search contacts..."
                                    className="flex-1 rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                                    autoFocus
                                />
                                <button
                                    onClick={handleSearchSupplierContacts}
                                    disabled={searchingSupplierContact}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {searchingSupplierContact ? 'Searching...' : 'Search'}
                                </button>
                            </div>

                            <div className="max-h-64 overflow-y-auto">
                                {supplierSearchResults.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">
                                        {supplierSearchTerm ? 'No contacts found' : 'Enter a search term to find contacts'}
                                    </p>
                                ) : (
                                    <ul className="divide-y divide-gray-200">
                                        {supplierSearchResults.map(contact => (
                                            <li
                                                key={contact.contactId}
                                                onClick={() => handleSelectSupplierContact(contact)}
                                                className="py-3 px-2 hover:bg-gray-50 cursor-pointer rounded"
                                            >
                                                <p className="font-medium text-gray-900">{contact.name}</p>
                                                <p className="text-xs text-gray-400 font-mono">{contact.contactId}</p>
                                                {contact.emailAddress && (
                                                    <p className="text-sm text-gray-500">{contact.emailAddress}</p>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => {
                                        setConfiguringSupplier(null);
                                        setSupplierSearchTerm('');
                                        setSupplierSearchResults([]);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Tax Type Selection Modal */}
            {selectingTaxType && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-center justify-center p-4">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setSelectingTaxType(null)}></div>
                        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Select Tax Type
                            </h3>

                            <div className="max-h-64 overflow-y-auto">
                                {loadingTaxTypes ? (
                                    <p className="text-sm text-gray-500 text-center py-4">Loading tax rates from Xero...</p>
                                ) : taxTypeOptions.length === 0 ? (
                                    <p className="text-sm text-gray-500 text-center py-4">No tax rates available. Please reconnect to Xero.</p>
                                ) : (
                                    <ul className="divide-y divide-gray-200">
                                        {taxTypeOptions.map(taxType => (
                                            <li
                                                key={taxType.code}
                                                onClick={() => handleSelectTaxType(taxType)}
                                                className="py-3 px-2 hover:bg-gray-50 cursor-pointer rounded"
                                            >
                                                <p className="font-medium text-gray-900">{taxType.name}</p>
                                                <p className="text-sm text-gray-500">Code: {taxType.code} | Rate: {taxType.rate}%</p>
                                                <p className="text-xs text-gray-400">
                                                    {taxType.canApplyToExpenses && 'Can apply to expenses'}
                                                    {taxType.canApplyToExpenses && taxType.canApplyToRevenue && ' | '}
                                                    {taxType.canApplyToRevenue && 'Can apply to revenue'}
                                                </p>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            <div className="mt-4 flex justify-end">
                                <button
                                    onClick={() => {
                                        setSelectingTaxType(null);
                                        setTaxTypeOptions([]);
                                    }}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Back Link */}
            <div className="mt-8">
                <a
                    href="/xero-documents"
                    className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                    &larr; Back to Xero Documents
                </a>
            </div>
        </div>
    );
}

// Helper component for contact mapping rows
function ContactMappingRow({ label, mapping, onConfigure }) {
    return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
                <p className="text-sm font-medium text-gray-700">{label}</p>
                {mapping ? (
                    <p className="text-sm text-green-600">{mapping.xero_contact_name}</p>
                ) : (
                    <p className="text-sm text-yellow-600">Not configured</p>
                )}
            </div>
            <button
                onClick={onConfigure}
                className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded hover:bg-indigo-200"
            >
                {mapping ? 'Change' : 'Configure'}
            </button>
        </div>
    );
}

// Helper component for account code rows
function AccountCodeRow({ label, currentCode, onConfigure }) {
    return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
                <p className="text-sm font-medium text-gray-700">{label}</p>
                {currentCode ? (
                    <p className="text-sm text-green-600">{currentCode}</p>
                ) : (
                    <p className="text-sm text-yellow-600">Not configured</p>
                )}
            </div>
            <button
                onClick={onConfigure}
                className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded hover:bg-indigo-200"
            >
                {currentCode ? 'Change' : 'Configure'}
            </button>
        </div>
    );
}

// Helper component for tax type rows
function TaxTypeRow({ label, currentCode, currentName, onConfigure }) {
    const displayValue = currentName || currentCode;
    return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
                <p className="text-sm font-medium text-gray-700">{label}</p>
                {currentCode ? (
                    <p className="text-sm text-green-600">{displayValue}</p>
                ) : (
                    <p className="text-sm text-yellow-600">Not configured (default: NONE)</p>
                )}
            </div>
            <button
                onClick={onConfigure}
                className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-100 rounded hover:bg-indigo-200"
            >
                {currentCode ? 'Change' : 'Configure'}
            </button>
        </div>
    );
}
